import { LitElement, css, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import type {
  ChatLabels,
  ChatScopeConfig,
  ScopeGroup,
  ScopeOption,
  WorkspaceScopeName,
} from "../types.js";

type AppbarTheme = "light" | "dark";

// White-on-dark wordmark: the appbar keeps its slate background in both themes.
const AFAS_LOGO_SRC = "/images/afas-help-logo-wit.svg";

// The wordmark costs 109px on a narrow bar, which the product switcher needs; the A
// mark says the same thing in 24.
const AFAS_LOGO_COMPACT_SRC = "/images/afas-logo-a-wit.svg";

/** Below this width the bar cannot carry the wordmark next to the product switcher. */
const COMPACT_MAX_WIDTH = 760;

/** Mirrors RAIL_WIDTH in jonas-chat-shell.ts: the conversations panel is a rail here. */
const RAIL_WIDTH = 56;

// The panel's divider takes a pixel off the right, so the rail centres its icons on 27.5.
const RAIL_BORDER = 1;

const COMPACT_APPBAR_PADDING = 8;

@customElement("help-jonas-chat-appbar")
export class HelpJonasChatAppbar extends LitElement {
  static override styles = css`
    :host {
      position: relative;
      z-index: 20;
      display: block;
      flex: 0 0 auto;
      height: 48px;
      min-height: 48px;
      background: #1e293b;
      color: #f1f5f9;
      border-bottom: 1px solid rgba(30, 31, 32, 0.08);
    }

    :host([theme="dark"]) {
      border-bottom-color: var(--color-foreground-300);
    }

    /* The appbar is workspace chrome; hide it on paper so Ctrl+P prints only the
       chat thread. The surfaces print in place via @media print CSS — there is
       no JS print controller staging a separate copy. */
    @media print {
      :host {
        display: none;
      }
    }

    /*
     * Three tracks with equal 1fr sides, so the middle one (the product switcher) is
     * centred on the BAR rather than on whatever space the logo leaves over. The sides
     * hold the logo and the status + user menu.
     */
    .appbar {
      display: grid;
      box-sizing: border-box;
      grid-template-columns: 1fr auto 1fr;
      align-items: center;
      gap: 12px;
      height: 48px;
      padding: 0 12px;
      overflow: visible;
    }

    .appbar-side {
      display: flex;
      min-width: 0;
      align-items: center;
      gap: 8px;
    }

    .appbar-side--right {
      justify-content: flex-end;
    }

    .appbar-center {
      display: flex;
      min-width: 0;
      align-items: center;
      justify-content: center;
    }

    .appbar-country {
      max-width: min(42vw, 320px);
    }

    .appbar-logo-button {
      display: inline-flex;
      align-items: center;
      flex: 0 0 auto;
      padding: 0;
      border: 0;
      background: none;
      cursor: pointer;
      border-radius: 4px;
    }

    .appbar-logo-button:focus-visible {
      outline: 2px solid #f1f5f9;
      outline-offset: 2px;
    }

    .appbar-logo {
      display: block;
      flex: 0 0 auto;
      width: min(168px, 34vw);
      height: 25px;
      object-fit: contain;
      /* The wordmark is narrower than the box it is given, and object-fit: contain would
         centre it there -- pushing it ~18px past the column the panel labels start at.
         Anchoring it left puts it on the appbar's own 12px padding, which is that column. */
      object-position: left center;
      user-select: none;
      pointer-events: none;
    }

    /*
     * The frame lives on a wrapper, not on afas-button-group: the group hands each
     * slotted button its corner radii through custom properties, so bordering the
     * group itself would fight rules from its own shadow root.
     */
    .scope-product {
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      border: 1px solid rgba(241, 245, 249, 0.35);
      border-radius: 6px;
    }

    .scope-product-group {
      flex: 0 0 auto;
      align-self: center;
    }

    /*
     * The active segment: a lighter plate, the way the bar reads a selected product.
     * afas-button's own toggled tint fills the whole segment edge to edge, so on this dark
     * bar it blends into the frame and the selection is barely readable. Until the button
     * group carries this itself, inset the plate by 2px and round it, which is what makes it
     * read as raised. The shorter --button-height keeps the frame at its original height.
     */
    .scope-product-group afas-button[state="toggled"] {
      --button-height: 28px;
      margin: 2px;
      background: rgba(241, 245, 249, 0.16);
      border-radius: 2px;
    }

    .scope-product-readonly {
      flex: 0 1 auto;
      max-width: min(28vw, 180px);
      min-width: 0;
      align-self: center;
      padding: 0 8px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      color: #f1f5f9;
      font-weight: 500;
    }

    .appbar afas-button,
    .appbar afas-dropdown-button {
      --foreground-color: #f1f5f9;
      --icon-color: #f1f5f9;
      align-self: center;
    }

    afas-dropdown-button.appbar-user-menu {
      display: inline-flex;
      align-items: center;
    }

    .user-menu-content {
      display: grid;
      gap: 10px;
      width: min(360px, calc(100vw - 24px));
      height: auto;
      max-height: min(460px, calc(100dvh - 72px));
      overflow: visible;
      color: var(--color-text-on-background-800);
      padding: 14px;
      min-width: 0;
    }

    .user-menu-content afas-field-row {
      min-width: 0;
      margin-bottom: 0;
    }

    /* The CMS logout renders nothing for non-editors but stays in the DOM as a
       grid item, adding a phantom row gap and bottom whitespace below the last
       visible option (most obvious when only the theme row is shown for SB).
       Drop it from the grid while empty so the menu hugs its content. */
    .user-menu-content help-jonas-cms-logout:empty {
      display: none;
    }

    .scope-select {
      min-width: 0;
    }

    .scope-select--language {
      width: clamp(112px, 11vw, 150px);
    }

    afas-switch {
      min-width: 0;
    }

    @media (max-width: ${COMPACT_MAX_WIDTH}px) {
      .appbar {
        padding: 0 ${COMPACT_APPBAR_PADDING}px;
      }

      /* The A mark brings its own 22:23 ratio, so height alone sizes it. Below the panel
         it is a rail of centred icons, so the box spans that rail and centres the mark on
         the same axis instead of carrying on with the left-aligned column. */
      .appbar-logo {
        width: ${RAIL_WIDTH - RAIL_BORDER - 2 * COMPACT_APPBAR_PADDING}px;
        object-position: center;
      }

      .scope-product-readonly {
        max-width: min(34vw, 140px);
      }

      .scope-select--language {
        width: 104px;
      }

      .appbar-country {
        max-width: 38vw;
      }
    }
  `;

  @property({ attribute: false })
  accessor scope: ChatScopeConfig | null = null;

  @property({ attribute: false })
  accessor labels: ChatLabels | null = null;

  @property({ reflect: true })
  accessor theme: AppbarTheme = "light";

  protected override render() {
    return html`
      <div
        class="appbar"
        role="toolbar"
        aria-label=${this.getLabel("workspaceContextLabel", "Chat context")}
      >
        <div class="appbar-side appbar-side--left">
          <button
            type="button"
            class="appbar-logo-button"
            aria-label=${this.getLabel("newChat", "Nieuw gesprek")}
            title=${this.getLabel("newChat", "Nieuw gesprek")}
            @click=${() => this.startNewChat()}
          >
            <picture>
              <source
                media="(max-width: ${COMPACT_MAX_WIDTH}px)"
                srcset=${AFAS_LOGO_COMPACT_SRC}
              />
              <img class="appbar-logo" src=${AFAS_LOGO_SRC} alt="AFAS" />
            </picture>
          </button>
        </div>
        <div class="appbar-center">${this.renderProductSwitcher()}</div>
        <div class="appbar-side appbar-side--right">
          <help-jonas-appbar-status
            .labels=${this.labels}
          ></help-jonas-appbar-status>
          ${this.renderUserMenu()}
        </div>
      </div>
    `;
  }

  // Clicking the AFAS logo starts a fresh conversation (the chat's start page). Reuses the
  // same bubbling event the "Nieuwe chat" button fires so the app resets via handleNewChat.
  private startNewChat(): void {
    this.dispatchEvent(
      new CustomEvent("jonas-new-chat", { bubbles: true, composed: true }),
    );
  }

  private renderUserMenu() {
    const label = this.getLabel("userMenuLabel", "Open gebruikersmenu");

    return html`
      <afas-dropdown-button
        class="appbar-user-menu"
        variant="minimal"
        tone="neutral"
        icon-only
        label=${label}
        tooltip=${label}
        placement="bottom-end"
        data-ai-chat-user-menu
      >
        <afas-icon slot="icon" icon="SettingsFilled" size="medium"></afas-icon>
        <div
          id="jonas-user-menu"
          class="user-menu-content"
          role="dialog"
          aria-label=${label}
          @click=${this.keepMenuOpen}
        >
          ${this.isSbProduct()
            ? nothing
            : html`
                ${this.renderUserMenuScopeField(
                  "helpCenter",
                  this.getLabel("helpCenterLabel", "Land"),
                  this.scope?.helpCenter,
                )}
                ${this.renderUserMenuScopeField(
                  "language",
                  this.getLabel("languageLabel", "Taal"),
                  this.scope?.language,
                )}
              `}
          <afas-field-row compact no-post-icon>
            <afas-label slot="label" for="jonas-theme-select">
              ${this.getLabel("themeToggleLabel", "Donkere modus")}
            </afas-label>
            <afas-switch
              id="jonas-theme-select"
              slot="value"
              compact
              label=${this.getLabel("themeToggleLabel", "Donkere modus")}
              .value=${this.theme === "dark"}
              @value=${this.themeChanged}
            ></afas-switch>
          </afas-field-row>
          <help-jonas-cms-logout></help-jonas-cms-logout>
        </div>
      </afas-dropdown-button>
    `;
  }

  /**
   * afas-dropdown closes on any click that reaches its panel slot, which suits a menu but
   * not this one: it holds selects and a switch the user has to operate. Stopping the click
   * inside the panel keeps the menu open; clicking outside still closes it.
   */
  private keepMenuOpen(event: Event): void {
    event.stopPropagation();
  }

  private renderScopeSelect(
    name: Extract<WorkspaceScopeName, "language" | "product">,
    label: string,
    group: ScopeGroup | undefined,
  ) {
    if (!group) return null;

    return html`
      <afas-select
        class=${`scope-select scope-select--${name}`}
        label=${label}
        value=${group.current.value}
        .clearable=${false}
        .displayOptions=${["label"]}
        @change=${(event: Event) => this.scopeChanged(name, event)}
      >
        ${this.getUniqueOptions(group).map(
          (option) =>
            html`<afas-menu-item
              role-variant="option"
              value=${option.value}
              label=${option.label}
              >${option.label}</afas-menu-item
            >`,
        )}
      </afas-select>
    `;
  }

  private renderProductSwitcher() {
    const group = this.scope?.product;
    if (!group) return null;
    const label = this.getLabel("productLabel", "Product");
    const options = this.getOrderedOptions(group);

    // A single product offers nothing to switch to, so render the value as a
    // static read-only label instead of a control that only repeats itself.
    if (options.length <= 1) {
      const text = this.getOptionText(group.current);
      return html`
        <span class="scope-product-readonly" aria-label=${label} title=${text}
          >${text}</span
        >
      `;
    }

    // afas-button-group defaults its own role to tablist; these are buttons that
    // navigate, not tabs, so claim the role before it is set.
    return html`
      <div class="scope-product">
        <afas-button-group
          class="scope-product-group"
          role="group"
          aria-label=${label}
        >
          ${options.map((option) => {
            const text = this.getOptionText(option);
            const isCurrent =
              option.current === true || option.value === group.current.value;
            // No tone: tone="neutral" would pin the label to --color-foreground-500, a
            // muted grey that disappears on the dark bar. The default tone resolves
            // --foreground-color, which the appbar sets to its own near-white.
            return html`<afas-button
              variant="minimal"
              state=${isCurrent ? "toggled" : "none"}
              label=${text}
              @click=${() =>
                this.scopeChangedByValue({ name: "product", option })}
            ></afas-button>`;
          })}
        </afas-button-group>
      </div>
    `;
  }

  private renderUserMenuScopeField(
    name: Extract<WorkspaceScopeName, "helpCenter" | "language">,
    label: string,
    group: ScopeGroup | undefined,
  ) {
    if (!group) return null;
    const id = `jonas-${name}-select`;

    return html`
      <afas-field-row compact no-post-icon>
        <afas-label slot="label" for=${id}>${label}</afas-label>
        <afas-select
          id=${id}
          slot="value"
          class=${`scope-select scope-select--${name}`}
          label=${label}
          value=${group.current.value}
          .clearable=${false}
          .displayOptions=${["label"]}
          @change=${(event: Event) => this.scopeChanged(name, event)}
        >
          ${this.getUniqueOptions(group).map(
            (option) =>
              html`<afas-menu-item
                role-variant="option"
                value=${option.value}
                label=${option.label}
                >${option.label}</afas-menu-item
              >`,
          )}
        </afas-select>
      </afas-field-row>
    `;
  }

  private renderCountrySwitcher() {
    const group = this.scope?.helpCenter;
    if (!group) return null;

    return html`
      <afas-dropdown-button
        class="appbar-country"
        variant="minimal"
        tone="neutral"
        label=${this.getOptionText(group.current)}
      >
        <afas-icon slot="icon" icon="Public"></afas-icon>
        <afas-menu>
          ${this.getUniqueOptions(group).map(
            (option) =>
              html`<afas-menu-item
                ?selected=${option.current === true ||
                option.value === group.current.value}
                @click=${() =>
                  this.scopeChangedByValue({ name: "helpCenter", option })}
                >${this.getOptionText(option)}</afas-menu-item
              >`,
          )}
        </afas-menu>
      </afas-dropdown-button>
    `;
  }

  private scopeChanged(name: WorkspaceScopeName, event: Event): void {
    const detailValue = (event as CustomEvent<{ value?: string }>).detail
      ?.value;
    const targetValue = (event.target as HTMLInputElement).value;
    this.dispatchScopeChange(name, detailValue ?? targetValue);
  }

  private scopeChangedByValue(input: {
    name: Extract<WorkspaceScopeName, "helpCenter" | "product">;
    option: ScopeOption;
  }): void {
    this.dispatchScopeChange(input.name, input.option.value);
  }

  private dispatchScopeChange(name: WorkspaceScopeName, value: string): void {
    this.dispatchEvent(
      new CustomEvent("jonas-scope-change", {
        detail: { name, value },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private themeChanged(event: Event): void {
    const detail = (event as CustomEvent<boolean | { value?: boolean }>).detail;
    const detailValue = typeof detail === "boolean" ? detail : detail?.value;
    const targetValue = (event.target as { value?: boolean }).value;
    const isDark =
      typeof detailValue === "boolean"
        ? detailValue
        : typeof targetValue === "boolean"
          ? targetValue
          : this.theme !== "dark";

    this.dispatchEvent(
      new CustomEvent("jonas-theme-toggle", {
        detail: { theme: isDark ? "dark" : "light" },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private getUniqueOptions(group: ScopeGroup): ScopeOption[] {
    const seenValues = new Set<string>();
    return [group.current, ...group.options].filter((option) => {
      if (seenValues.has(option.value)) return false;
      seenValues.add(option.value);
      return true;
    });
  }

  private getOrderedOptions(group: ScopeGroup): ScopeOption[] {
    const seenValues = new Set<string>();
    return [...group.options, group.current].filter((option) => {
      if (seenValues.has(option.value)) return false;
      seenValues.add(option.value);
      return true;
    });
  }

  private isSbProduct(): boolean {
    // SB is only published for the Netherlands in Dutch, so the country and
    // language pickers offer nothing to switch to. Hide them while SB is the
    // active product instead of showing locked single-option dropdowns.
    return this.scope?.product?.current.value?.toLowerCase() === "sb";
  }

  private getOptionText(option: ScopeOption): string {
    return option.shortLabel?.trim() || option.label?.trim() || option.value;
  }

  private getLabel(key: string, fallback: string): string {
    const value = this.labels?.[key]?.trim();
    return value || fallback;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "help-jonas-chat-appbar": HelpJonasChatAppbar;
  }
}
